<template>
  <div class="view-signup">
    <v-row justify="center">
      <v-col cols="12" sm="8" md="8">
        <v-card class="elevation-12">
          <v-toolbar color="primary" dark flat>
            <v-toolbar-title>{{ $t('registration.title') }}</v-toolbar-title>
            <v-spacer />
          </v-toolbar>
          <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
            <v-card-text>
              <!-- Alert utilizzato in caso di errore -->
              <v-alert
                type="error" dismissible v-model="alert" class="mb-4"
                transition="scale-transition">
                {{ error }}
              </v-alert>

              <!-- Alert utilizzato in caso di successo -->
              <v-alert
                type="success" dismissible v-model="success" class="mb-4"
                transition="scale-transition">
                {{ $t('registration.successMessage') }}
              </v-alert>

              <p v-html="$t('registration.description')"></p>

              <!-- 1. Country / Region + Language (OBBLIGATORI) -->
              <v-row>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="country"
                    :disabled="submitting"
                    :items="countries"
                    item-text="text"
                    item-value="value"
                    :label="$t('registration.fields.country')"
                    :placeholder="country ? $t('registration.fields.countryPlaceholder') : $t('registration.detection.detecting')"
                    hide-details
                    filled
                    required
                    :rules="[(v) => !!v || $t('registration.validation.countryRequired')]"
                    :loading="country === null"
                  >
                    <template v-slot:selection="{ item }">
                      {{ getCountryFlag(item.value) }} {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                      {{ getCountryFlag(item.value) }} {{ item.text }}
                    </template>
                    <template v-slot:append>
                      <v-icon v-if="country === null" class="animate-spin">mdi-loading</v-icon>
                      <v-icon v-else>mdi-earth</v-icon>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="language"
                    :disabled="submitting"
                    :items="languages"
                    item-text="text"
                    item-value="value"
                    :label="$t('registration.fields.language')"
                    :placeholder="$t('registration.fields.languagePlaceholder')"
                    hide-details
                    filled
                    required
                    :rules="[(v) => !!v || $t('registration.validation.languageRequired')]"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <!-- 2. User Type (OBBLIGATORIO) -->
              <v-autocomplete
                ref="type"
                v-model="type"
                :disabled="submitting"
                :items="userTypes"
                :label="$t('registration.fields.type')"
                :placeholder="$t('registration.fields.typePlaceholder')"
                hide-details
                filled
                required
                :rules="[(v) => !!v || $t('registration.validation.typeRequired')]"
              ></v-autocomplete>

              <v-row>
                <!-- 3. Company Name (se hasCompany) -->
                <v-col cols="12" v-if="hasCompany" class="pb-0">
                  <v-text-field
                    ref="company"
                    v-model="company"
                    :disabled="submitting"
                    :label="$t('registration.fields.company')"
                    :rules="[v => !!v || $t('registration.validation.companyRequired')]"
                  ></v-text-field>
                </v-col>

                <!-- 4-5. First Name + Last Name -->
                <v-col cols="12" sm="6" :class="{ 'pt-0': hasCompany }">
                  <v-text-field
                    ref="firstName"
                    v-model="firstName"
                    :disabled="submitting"
                    :label="$t('registration.fields.firstName')"
                    :rules="[v => !!v || $t('registration.validation.firstNameRequired')]"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" :class="{ 'pt-0': hasCompany }">
                  <v-text-field
                    v-model="lastName"
                    :disabled="submitting"
                    :label="$t('registration.fields.lastName')"
                    :rules="[v => !!v || $t('registration.validation.lastNameRequired')]"
                  ></v-text-field>
                </v-col>

                <!-- 6-7. Email + Phone -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    ref="email"
                    v-model="email"
                    :disabled="submitting"
                    :label="$t('registration.fields.email')"
                    prepend-icon="mdi-email"
                    type="email"
                    :rules="[v => !!v || $t('registration.validation.emailRequired')]"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="phone"
                    :disabled="submitting"
                    :label="$t('registration.fields.phone')"
                    prepend-icon="mdi-phone"
                    type="tel"
                    :rules="[v => !!v || $t('registration.validation.phoneRequired')]"
                  />
                </v-col>

                <!-- 8-9. Password + Confirm Password -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="password"
                    :disabled="submitting"
                    :label="$t('registration.fields.password')"
                    prepend-icon="mdi-lock"
                    type="password"
                    :rules="[v => !!v || $t('registration.validation.passwordRequired')]"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="passwordConfirm"
                    :disabled="submitting"
                    :label="$t('registration.fields.passwordConfirm')"
                    prepend-icon="mdi-lock"
                    type="password"
                    :rules="[v => !!v || $t('registration.validation.passwordConfirmRequired')]"
                  />
                </v-col>

                <!-- 10. Address line 1 -->
                <v-col cols="12" class="pt-0">
                  <v-text-field
                    v-model="address"
                    :disabled="submitting"
                    :label="$t('registration.fields.address')"
                  ></v-text-field>
                </v-col>

                <!-- 11. Address line 2 (optional) -->
                <v-col cols="12" class="pt-0">
                  <v-text-field
                    v-model="address2"
                    :disabled="submitting"
                    :label="$t('registration.fields.address2')"
                    :hint="$t('registration.fields.address2Hint')"
                    persistent-hint
                  ></v-text-field>
                </v-col>

                <!-- 12-14. Postal Code + City + Province/Region -->
                <v-col cols="12" :sm="showProvince ? 3 : 4">
                  <v-text-field
                    v-model="postalCode"
                    :disabled="submitting"
                    :label="postalCodeLabel"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" :sm="showProvince ? 4 : 8">
                  <v-text-field
                    v-model="city"
                    :disabled="submitting"
                    :label="$t('registration.fields.city')"
                  ></v-text-field>
                </v-col>

                <!-- Province - Solo se IT -->
                <v-col cols="12" sm="5" v-if="showProvince">
                  <v-autocomplete
                    v-model="province"
                    :disabled="submitting"
                    :items="provinces"
                    clearable
                    :label="$t('registration.fields.province')"
                    :rules="[v => !!v || $t('registration.validation.provinceRequired')]"
                  ></v-autocomplete>
                </v-col>

                <!-- Phone 1 (optional) -->
                <v-col cols="12">
                  <v-text-field
                    v-model="phone1"
                    :disabled="submitting"
                    :label="$t('registration.fields.phoneOptional')"
                    prepend-icon="mdi-phone"
                    type="tel"
                  />
                </v-col>

                <v-col cols="12">
                  <v-checkbox
                    v-model="checkbox1"
                    color="success"
                    required
                    :rules="[(v) => !!v || $t('registration.validation.privacyRequired')]"
                  >
                    <template v-slot:label>
                      <div>
                        {{ privacyText.beforeLink }}<router-link :to="{ name: 'Privacy' }">{{ privacyText.linkText }}</router-link>{{ privacyText.afterLink }}
                      </div>
                    </template>
                  </v-checkbox>
                </v-col>

                <v-col cols="12">
                  <v-checkbox
                    v-model="checkbox2"
                    :label="$t('registration.checkboxes.marketing')"
                    color="success"
                  ></v-checkbox>
                </v-col>
              </v-row>

              <p class="mt-4" v-html="$t('registration.finalMessage')"></p>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn type="submit" color="success"
                :disabled="!valid || submitting"
                :loading="submitting"
                @click="onSignup">{{ $t('registration.submitButton') }}</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

  </div>
</template>

<script>
/* eslint-disable quote-props */

import { getProvince } from '@/utils/italy';
import { getCountries } from '@/utils/countries';
import { LANGUAGES } from '@/utils/languages';
import { SIGNUP_USER } from '@/store/actions/users';
import { LOGOUT } from '@/store/actions/auth';

export default {
  name: 'Registrati',

  data: () => ({
    type: null,
    country: null, // Rilevato automaticamente
    language: null, // Auto-detected based on country
    firstName: '',
    lastName: '',
    company: '',

    address: '',
    address2: '', // NUOVO - Address line 2
    postalCode: '',
    city: '',
    province: null,

    phone: '',
    phone1: '',
    email: '',
    password: '',
    passwordConfirm: '',

    checkbox1: false,
    checkbox2: false,

    submitting: false,
    valid: true,
    alert: false,
    success: false,
    error: '',
  }),

  async created() {
    console.log(`${this.$options.name}: created()`); // eslint-disable-line no-console

    // Effettua sempre il logout
    this.$store.dispatch(LOGOUT);

    // Rileva automaticamente la nazione
    await this.detectCountry();

    // Sposta il focus sull'input type (tipologia utente) e apre il menu
    this.$nextTick(() => {
      this.$refs.type.focus();
      this.$refs.type.activateMenu();
    });
  },

  watch: {
    country(newCountry) {
      // Auto-detect language based on country
      if (newCountry) {
        this.language = newCountry === 'IT' ? 'it' : 'en';
      }
    },

    type(newType) {
      // Sposta il focus quando viene selezionata la tipologia
      if (newType) {
        this.$nextTick(() => {
          if (this.hasCompany && this.$refs.company) {
            // Se è un'azienda, focus su company
            this.$refs.company.focus();
          } else if (!this.hasCompany && this.$refs.firstName) {
            // Se è privato, focus su firstName
            this.$refs.firstName.focus();
          }
        });
      }
    },
  },

  computed: {
    provinces: () => getProvince(),

    countries() {
      return getCountries(this.$i18n.locale);
    },

    languages() {
      return LANGUAGES;
    },

    userTypes() {
      return [
        { value: 'privato', text: this.$t('registration.userTypes.private') },
        { value: 'rivenditore', text: this.$t('registration.userTypes.retailer') },
        { value: 'venditore-esterno', text: this.$t('registration.userTypes.externalSeller') },
        { value: 'agente', text: this.$t('registration.userTypes.agent') },
        { value: 'imbianchino', text: this.$t('registration.userTypes.painter') },
        { value: 'progettista', text: this.$t('registration.userTypes.designer') },
        { value: 'keim', text: this.$t('registration.userTypes.keim') },
      ];
    },

    hasCompany() {
      return ['rivenditore', 'venditore-esterno', 'agente', 'imbianchino', 'progettista', 'keim']
        .includes(this.type);
    },

    showProvince() {
      return this.country === 'IT';
    },

    postalCodeLabel() {
      // Dinamico in base al paese
      if (this.country === 'IT') return this.$t('registration.postalCodeLabels.IT');
      if (this.country === 'US') return this.$t('registration.postalCodeLabels.US');
      return this.$t('registration.postalCodeLabels.default');
    },

    privacyText() {
      return {
        beforeLink: this.$t('registration.privacyCheckbox.beforeLink'),
        linkText: this.$t('registration.privacyCheckbox.linkText'),
        afterLink: this.$t('registration.privacyCheckbox.afterLink'),
      };
    },
  },

  methods: {
    async detectCountry() {
      try {
        // 1. Prova geolocalizzazione IP (più accurata)
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.country_code) {
          this.country = data.country_code;
          this.language = data.country_code === 'IT' ? 'it' : 'en'; // Auto-set language
          console.log(`Paese rilevato da IP: ${data.country_code} (${data.country_name})`);
          return;
        }
      } catch (error) {
        console.warn('Errore geolocalizzazione IP:', error);
      }

      try {
        // 2. Fallback: rileva da lingua browser
        const browserLang = navigator.language || navigator.languages[0];
        const langCode = browserLang.split('-')[0].toLowerCase();

        // Mappa lingue comuni a paesi
        const langToCountry = {
          'it': 'IT',
          'en': 'GB', // Default inglese a UK
          'de': 'DE',
          'fr': 'FR',
          'es': 'ES',
          'pt': 'PT',
          'nl': 'NL',
          'pl': 'PL',
          'ru': 'RU',
          'zh': 'CN',
          'ja': 'JP',
          'ko': 'KR',
          'ar': 'SA',
        };

        if (langToCountry[langCode]) {
          this.country = langToCountry[langCode];
          this.language = langToCountry[langCode] === 'IT' ? 'it' : 'en'; // Auto-set language
          console.log(`Paese rilevato da lingua browser: ${this.country} (${browserLang})`);
          return;
        }
      } catch (error) {
        console.warn('Errore rilevamento lingua:', error);
      }

      // 3. Fallback finale: Italia
      this.country = 'IT';
      this.language = 'it'; // Italian language for Italy
      console.log('Usando fallback: Italia');
    },

    getCountryFlag(countryCode) {
      // Importare da utils/countries
      if (!countryCode || countryCode.length !== 2) return '';
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => (127397 + char.charCodeAt()));
      return String.fromCodePoint(...codePoints);
    },

    async onSignup() {
      const formRef = this.$refs.form;

      if (!formRef.validate()) return;

      this.submitting = true;
      this.alert = false;
      this.success = false;
      this.error = '';

      if (this.password !== this.passwordConfirm) {
        this.error = this.$t('registration.validation.passwordsMatch');
        this.alert = true;
        this.submitting = false;
        return;
      }

      // Costruisce l'oggetto userData
      const userData = {
        type: this.type,
        country: this.country,
        language: this.language, // NEW
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        password: this.password,
        phone: this.phone,
        phone1: this.phone1,
        address: this.address,
        address2: this.address2,
        city: this.city,
        postalCode: this.postalCode,
        checkbox1: this.checkbox1,
        checkbox2: this.checkbox2,
      };

      // Aggiunge company solo se hasCompany
      if (this.hasCompany) {
        userData.company = this.company;
      }

      // Aggiunge province solo se IT
      if (this.country === 'IT') {
        userData.province = this.province;
      }

      try {
        await this.$store.dispatch(`users/${SIGNUP_USER}`, userData);

        // Successo
        this.submitting = false;
        this.success = true;

        // Reset form (mantiene paese rilevato)
        this.type = null;
        // this.country rimane invariato (mantiene paese rilevato)
        this.language = this.country === 'IT' ? 'it' : 'en'; // Reset based on country
        this.company = '';
        this.firstName = '';
        this.lastName = '';
        this.address = '';
        this.address2 = '';
        this.postalCode = '';
        this.city = '';
        this.province = null;
        this.phone = '';
        this.phone1 = '';
        this.email = '';
        this.password = '';
        this.passwordConfirm = '';
        this.checkbox1 = false;
        this.checkbox2 = false;
        formRef.resetValidation();

        this.$nextTick(() => {
          this.$refs.type.focus();
          this.$refs.type.activateMenu();
        });
      } catch (error) {
        console.error(`${this.$options.name}.onSignup() error =`, error); // eslint-disable-line no-console

        this.submitting = false;

        switch (error) {
          case 'auth/email-already-exists':
            this.error = this.$t('registration.errors.emailExists');
            break;
          default:
            this.error = this.$t('registration.errors.unknown', { error });
            break;
        }

        this.alert = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.view-signup {
  flex-grow: 1;
  padding-top: 2rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
